import React, { useState } from 'react';
import { MapPin, Clock, Send, Instagram, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha: '',
    personas: '2',
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/reservas`, formData);
      
      if (response.status === 201) {
        toast.success('¡Reserva enviada con éxito! Te contactaremos pronto.');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          fecha: '',
          personas: '2',
          mensaje: ''
        });
      }
    } catch (error) {
      console.error('Error al enviar reserva:', error);
      toast.error('Error al enviar la reserva. Por favor, intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Vení a Visitarnos
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Reservá tu mesa o pasá a conocernos
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Reservar Mesa
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <Input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Tu nombre"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="tu@email.com"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <Input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  required
                  placeholder="341 123 4567"
                  className="w-full"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha *
                  </label>
                  <Input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleChange}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personas *
                  </label>
                  <Input
                    type="number"
                    name="personas"
                    value={formData.personas}
                    onChange={handleChange}
                    required
                    min="1"
                    max="20"
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje (opcional)
                </label>
                <Textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Alguna preferencia o requerimiento especial..."
                  rows={4}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C1272D] hover:bg-[#A01F25] text-white py-6 text-lg transition-colors"
              >
                <Send className="mr-2" size={20} />
                {isSubmitting ? 'Enviando...' : 'Enviar Reserva'}
              </Button>
            </form>
          </div>

          {/* Info and Map */}
          <div className="space-y-8">
            {/* Contact Info */}
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Información de Contacto
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="text-[#C1272D] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">Dirección</p>
                    <p className="text-gray-600">Brown 2408, Rosario, Santa Fe</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="text-[#C1272D] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">Horarios</p>
                    <p className="text-gray-600">Martes a domingo</p>
                    <p className="text-gray-600">12:00 - 01:00</p>
                    <p className="text-gray-500 text-sm mt-1">Lunes cerrado</p>
                  </div>
                </div>

                <a
                  href="https://instagram.com/obra.pomodoro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 hover:opacity-80 transition-opacity"
                >
                  <Instagram className="text-[#C1272D] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">Instagram</p>
                    <p className="text-gray-600">@obra.pomodoro</p>
                  </div>
                </a>
              </div>

              <a
                href="https://www.google.com/maps/search/?api=1&query=Obrador+Pomodoro+Rosario"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C1272D] hover:bg-[#A01F25] text-white py-4 px-6 rounded-lg font-semibold transition-colors"
              >
                <Navigation size={20} />
                Cómo llegar
              </a>
            </div>

            {/* Google Map */}
            <div className="bg-white p-4 rounded-2xl shadow-xl">
              <div className="w-full h-80 rounded-xl overflow-hidden">
                <iframe
                  title="Ubicación Obrador Pomodoro"
                  src="https://maps.google.com/maps?q=Obrador+Pomodoro+Rosario&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
